import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // Common utility methods for DOM manipulation
  showElement(element) {
    if (element) {
      element.classList.remove("hidden")
    }
  }

  hideElement(element) {
    if (element) {
      element.classList.add("hidden")
    }
  }

  toggleElement(element, show = null) {
    if (element) {
      if (show === null) {
        element.classList.toggle("hidden")
      } else if (show) {
        this.showElement(element)
      } else {
        this.hideElement(element)
      }
    }
  }

  // Loading state management
  showLoading() {
    if (this.hasLoadingTarget) {
      this.showElement(this.loadingTarget)
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.hideElement(this.loadingTarget)
    }
  }

  // Form submission helpers
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }

  // Common headers for API requests
  getAPIHeaders(includeJSON = true) {
    const headers = {
      'X-CSRF-Token': this.getCSRFToken()
    }
    
    if (includeJSON) {
      headers['Accept'] = 'application/json'
    }
    
    return headers
  }

  // Error handling and display
  showErrors(errors) {
    if (this.hasErrorsTarget && this.hasErrorListTarget) {
      this.errorListTarget.innerHTML = ""
      
      errors.forEach(error => {
        const li = document.createElement("li")
        li.textContent = error
        this.errorListTarget.appendChild(li)
      })
      
      this.showElement(this.errorsTarget)
    }
  }

  hideErrors() {
    if (this.hasErrorsTarget) {
      this.hideElement(this.errorsTarget)
    }
  }

  // Modal management
  showModal(modalId) {
    const modal = document.getElementById(modalId)
    if (modal) {
      this.showElement(modal)
      document.body.classList.add("overflow-hidden")
    }
  }

  hideModal(modalId) {
    const modal = document.getElementById(modalId)
    if (modal) {
      this.hideElement(modal)
      document.body.classList.remove("overflow-hidden")
    }
  }

  closeModal() {
    // Try to find and close any visible modals
    const modals = document.querySelectorAll('[id$="-modal"]:not(.hidden)')
    modals.forEach(modal => {
      this.hideElement(modal)
    })
    document.body.classList.remove("overflow-hidden")
  }

  // Common input creation helpers
  createHiddenInput(name, value, id = null) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    if (id) {
      input.id = id
    }
    return input
  }

  // Common button creation helpers
  createButton(text, className, clickHandler) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = className
    button.textContent = text
    if (clickHandler) {
      button.addEventListener("click", clickHandler)
    }
    return button
  }

  // SVG icon helpers
  getEditIcon() {
    return `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
      </svg>
    `
  }

  getRemoveIcon() {
    return `
      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
    `
  }

  // API communication helpers
  async fetchData(url, options = {}) {
    const defaultOptions = {
      headers: this.getAPIHeaders()
    }
    
    const mergedOptions = { ...defaultOptions, ...options }
    
    try {
      const response = await fetch(url, mergedOptions)
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return { response, data, success: response.ok }
      } else {
        const text = await response.text()
        console.error('Non-JSON response:', text)
        return { 
          response, 
          data: { errors: ['An unexpected error occurred. Please try again.'] }, 
          success: false 
        }
      }
    } catch (error) {
      console.error('Fetch error:', error)
      return { 
        response: null, 
        data: { errors: ['A network error occurred. Please try again.'] }, 
        success: false 
      }
    }
  }

  // Form validation helpers
  validateRequired(value, fieldName) {
    if (!value || !value.toString().trim()) {
      return `${fieldName} is required`
    }
    return null
  }

  validateFormat(value, pattern, fieldName, formatDescription) {
    if (value && !pattern.test(value)) {
      return `${fieldName} ${formatDescription}`
    }
    return null
  }

  // Common form loading state management
  setSubmitButtonLoading(loading = true) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
      
      if (this.hasSubmitTextTarget && this.hasSubmitSpinnerTarget) {
        if (loading) {
          this.hideElement(this.submitTextTarget)
          this.showElement(this.submitSpinnerTarget)
        } else {
          this.showElement(this.submitTextTarget)
          this.hideElement(this.submitSpinnerTarget)
        }
      }
    }
  }

  // Debounce utility for search inputs
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Common dropdown event handling
  setupOutsideClickHandler(dropdownElement, callback) {
    const handler = (event) => {
      if (!this.element.contains(event.target)) {
        callback(event)
      }
    }
    
    document.addEventListener("click", handler)
    
    // Return cleanup function
    return () => {
      document.removeEventListener("click", handler)
    }
  }

  // Artist tag creation helper (common pattern across search controllers)
  createArtistTag(artist, editHandler, removeHandler, className = "bg-gray-100 text-gray-900") {
    const artistTag = document.createElement("div")
    artistTag.className = `flex items-center justify-between w-full ${className} text-sm font-medium px-3 py-2 rounded-md border border-gray-300`
    artistTag.innerHTML = `
      <span>${artist.name}</span>
      <div class="flex items-center space-x-2">
        <button type="button" class="text-gray-900 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors cursor-pointer" data-action="click" data-artist-id="${artist.id}" data-artist-name="${artist.name}">
          ${this.getEditIcon()}
        </button>
        <button type="button" class="text-gray-900 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors cursor-pointer" data-artist-id="${artist.id}">
          ${this.getRemoveIcon()}
        </button>
      </div>
    `
    
    // Add event listeners
    const editButton = artistTag.querySelector("button[data-action='click']")
    const removeButton = artistTag.querySelectorAll("button")[1]
    
    if (editHandler) {
      editButton.addEventListener("click", editHandler)
    }
    
    if (removeHandler) {
      removeButton.addEventListener("click", removeHandler)
    }
    
    return { artistTag, editButton, removeButton }
  }

  // Common search results display helper
  displaySearchResults(artists, resultsContainer, onArtistSelect, addNewArtistHandler) {
    resultsContainer.innerHTML = ""

    if (artists.length === 0) {
      resultsContainer.innerHTML = `<li class="px-3 py-2 text-sm text-gray-500">${I18n.t('artists.no_artists_found')}</li>`
    } else {
      artists.forEach(artist => {
        const li = document.createElement("li")
        li.className = "px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
        li.innerHTML = `
          <div class="font-medium text-gray-900">${artist.name}</div>
        `
        li.addEventListener("click", () => onArtistSelect(artist))
        resultsContainer.appendChild(li)
      })
    }
    
    // Add separator and "Add a New Artist" link
    if (addNewArtistHandler) {
      const separatorLi = document.createElement("li")
      separatorLi.className = "border-t border-gray-200 mt-1"
      separatorLi.innerHTML = `
        <button type="button" class="w-full px-3 py-2 text-sm text-center text-gray-900 hover:text-gray-600 hover:bg-gray-50 font-medium cursor-pointer">
          ${I18n.t('artists.add_new_artist')}
        </button>
      `
      separatorLi.querySelector("button").addEventListener("click", addNewArtistHandler)
      resultsContainer.appendChild(separatorLi)
    }
  }

  // Form reset helper
  resetForm() {
    if (this.hasFormTarget) {
      this.formTarget.reset()
    }
    this.hideErrors()
  }
}